/**
 * Task Event Payload Schemas
 *
 * Zod schemas for task-related Socket.io event payloads.
 */

import { z } from 'zod';
import { AccessLevelSchema, PermissionModeSchema, SessionStatusSchema } from './common.schema.js';
import { PREVIEW_PATH_MAX } from '../types/live-preview.js';

// =============================================================================
// Reusable Task Sub-Schemas
// =============================================================================

export const TaskOutputTypeSchema = z.enum(['link', 'file', 'image']);

export const TaskOutputSchema = z.object({
  id: z.string().min(1),
  title: z.string(),
  url: z.string(),
  type: TaskOutputTypeSchema,
  category: z.string().optional(),
  createdAt: z.string(),
  /** Full sandbox path, e.g. "/app/workspace/files/painting.png" — the TaskOutput
   *  type and every server emitter (sandbox-relay registerUserFileAsTaskOutput /
   *  UserFileEvent) carry it; the schema had silently dropped it. */
  filePath: z.string().optional(),
  /** MIME type, e.g. "image/png" — same provenance as filePath. */
  mediaType: z.string().optional(),
});

/** tasks.lifecycle_state — mirrors TaskLifecycleState (types/task.ts). */
export const TaskLifecycleStateSchema = z.enum([
  'scheduled', 'queued', 'idle', 'processing', 'awaiting', 'error', 'completed',
]);

/** tasks.status — the ROW status the server persists and streams (Task.status in
 *  task.service.ts). Not SessionStatusSchema: 'completed' / 'error' are lifecycle
 *  states, never a task row status. */
export const TaskRowStatusSchema = z.enum(['active', 'paused', 'destroyed']);

export const TaskTodoGroupSchema = z.object({
  turnIndex: z.number(),
  todos: z.array(z.object({
    content: z.string(),
    status: z.enum(['pending', 'in_progress', 'completed']),
    activeForm: z.string().optional(),
  })),
  createdAt: z.string(),
});

/**
 * Schema for SerializedTask - the JSON transport format of a Task.
 * Many task event payloads reference this.
 */
export const SerializedTaskSchema = z.object({
  id: z.string().min(1),
  userId: z.string().min(1),
  agentId: z.string().min(1),
  status: SessionStatusSchema, // TaskStatus reuses the same enum values as SessionStatus
  permissionMode: PermissionModeSchema,
  // Permissions v2 per-task override; null/absent = inherit the user default. Surfaced
  // so the web picker can render the task's level (and the "(default)" inherit marker).
  accessLevel: AccessLevelSchema.nullable().optional(),
  routineId: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastActivityAt: z.string(),
  title: z.string().optional(),
  outputs: z.array(TaskOutputSchema).optional(),
  todos: z.array(TaskTodoGroupSchema).optional(),
});

/**
 * @deprecated The dead live-Task shape (agentId, pendingInput, Date fields).
 * task:stream never carried it (see TaskStreamPayloadSchema); no event schema
 * references it now. Retires with SerializedTaskSchema when the tasks:list DTO
 * (apps/server serialize-task.ts) moves into shared.
 */
export const TaskSchema = z.object({
  id: z.string().min(1),
  userId: z.string().min(1),
  agentId: z.string().min(1),
  status: SessionStatusSchema,
  permissionMode: PermissionModeSchema,
  // Permissions v2 per-task override; null/absent = inherit the user default.
  accessLevel: AccessLevelSchema.nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  lastActivityAt: z.date(),
  title: z.string().optional(),
  pendingInput: z.object({
    type: z.enum(['question', 'approval', 'permission']),
    data: z.unknown(),
    answers: z.record(z.string(), z.string()).optional(),
    approved: z.boolean().optional(),
  }).optional(),
  outputs: z.array(TaskOutputSchema).optional(),
  todos: z.array(TaskTodoGroupSchema).optional(),
  routineId: z.string().optional(),
});

// =============================================================================
// Task Event Payload Schemas
// =============================================================================

/**
 * task:created — THE WIRE, transcribed from every server emitter (2026-09-03):
 * handlers/socket/user-message-drive.ts, message-handlers.ts, gateway/forwarding.ts,
 * services/composer/dispatch.ts, services/routine-executor.service.ts. The task is
 * a MINIMAL announce (id / userId / title / agentSlug / status / createdAt /
 * updatedAt, routineId for routine-born tasks), never the tasks:list DTO — the
 * previous schema embedded the dead SerializedTask shape (agentId, permissionMode)
 * that no emitter ever sent. handlers/socket/orchestrator.ts (browser session)
 * used to emit { taskId, title, agentSlug } with NO task object — fixed in s5-02a,
 * where every emitter literal is `satisfies TaskCreatedPayload` so a dropped
 * `task` fails the server typecheck instead of the web handler.
 */
export const TaskCreatedPayloadSchema = z.object({
  // Client-Generated ID Flow (Phase 1 — backwards compatible):
  // - New clients: correlationId omitted. Matching is unnecessary because
  //   the client already knows the taskId it generated.
  // - Legacy clients: correlationId echoed back for ack-side matching.
  // Phase 3 will remove correlationId and require taskId at the top level.
  correlationId: z.string().min(1).optional(),  // LEGACY — removed in Phase 3
  taskId: z.string().min(1),
  task: z.object({
    id: z.string().min(1),
    userId: z.string().min(1),
    /** null on every user-initiated create; the routine's name on a routine run. */
    title: z.string().nullable(),
    agentSlug: z.string().min(1),
    status: TaskRowStatusSchema,
    routineId: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
});

export const TaskResumePayloadSchema = z.object({
  taskId: z.string().min(1),
});

/**
 * task:resumed — THE WIRE (handlers/socket/task-handlers.ts, the only emitter): a
 * minimal task with the hydrated pendingDetails, plus taskId at the top level.
 * pendingDetails is the server's PendingDetails superset — validated by its
 * discriminator only; the reason-specific fields pass through.
 */
export const TaskResumedPayloadSchema = z.object({
  task: z.object({
    id: z.string().min(1),
    userId: z.string().min(1),
    title: z.string(),
    agentSlug: z.string().min(1),
    lifecycleState: TaskLifecycleStateSchema,
    pendingDetails: z.object({ reason: z.string().min(1) }).passthrough().optional(),
  }),
  taskId: z.string().min(1),
});

export const TaskListPayloadSchema = z.object({
  tasks: z.array(SerializedTaskSchema),
});

/**
 * task:update — THE WIRE, from all three emitters (user-message-drive.ts title-gen,
 * task-handlers.ts TASK_TITLE_EDIT, session-spawn.service.ts): a title change,
 * nothing else. The previous schema said { task: SerializedTask }, which nothing
 * sent.
 */
export const TaskUpdatePayloadSchema = z.object({
  taskId: z.string().min(1),
  title: z.string(),
  timestamp: z.string(),
});

export const TaskDestroyPayloadSchema = z.object({
  taskId: z.string().min(1),
});

export const TaskTitleEditPayloadSchema = z.object({
  taskId: z.string().min(1),
  title: z.string(),
});

export const TaskSyncHintPayloadSchema = z.object({
  taskId: z.string().min(1),
  reason: z.string(),
});

/** W4-2 — the shadow injection screen's banner payload (server → frontend). */
export const InjectionScreenNoticePayloadSchema = z.object({
  taskId: z.string().min(1),
  toolUseId: z.string(),
  toolName: z.string(),
  family: z.string(),
  /** The screener's one-line rationale (bounded server-side; display-optional). */
  reason: z.string(),
  /** ISO timestamp of the verdict. */
  at: z.string(),
});
export type InjectionScreenNoticePayload = z.infer<typeof InjectionScreenNoticePayloadSchema>;

export const TaskResumeFailedPayloadSchema = z.object({
  taskId: z.string().min(1),
  error: z.string(),
});

export const TaskStatsPayloadSchema = z.object({
  taskId: z.string().min(1),
  stats: z.object({
    messageCount: z.number(),
    toolUseCount: z.number(),
    duration: z.number().optional(),
  }),
});

/**
 * task:stream — THE WIRE, field for field from services/task-stream.service.ts
 * (TaskStreamPayload + transformTask / transformOldTask): a Supabase Realtime row
 * change, NOT the live Task object with Date fields the previous schema described,
 * and the previous row is `oldTask`, never `old`. Nullable columns arrive as null
 * from Realtime (the server type says optional; the wire says null), so both are
 * accepted.
 */
const TaskStreamRowSchema = z.object({
  id: z.string().min(1),
  userId: z.string().min(1),
  agentSlug: z.string().min(1),
  parentSessionId: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  status: TaskRowStatusSchema,
  lifecycleState: TaskLifecycleStateSchema.nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  errorCode: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastActivityAt: z.string(),
});

export const TaskStreamPayloadSchema = z.object({
  eventType: z.enum(['INSERT', 'UPDATE', 'DELETE']),
  task: TaskStreamRowSchema,
  oldTask: z.object({
    id: z.string().min(1),
    userId: z.string().min(1),
    agentSlug: z.string().min(1),
    title: z.string().nullable().optional(),
    status: TaskRowStatusSchema,
    lifecycleState: TaskLifecycleStateSchema.nullable().optional(),
  }).optional(),
});

export const TaskOutputRegisteredPayloadSchema = z.object({
  taskId: z.string().min(1),
  outputs: z.array(TaskOutputSchema),
});

/** One presented artifact landed (Task Outputs / Pieces). `artifact` is the
 * MediaArtifact wire shape; validated loosely here (id + kind) so additive wire
 * growth never breaks event forwarding. */
export const TaskArtifactRegisteredPayloadSchema = z.object({
  taskId: z.string().min(1),
  artifact: z.object({ id: z.string().min(1), kind: z.string().min(1) }).passthrough(),
});

export const TaskPreviewReadyPayloadSchema = z.object({
  taskId: z.string().min(1),
  url: z.string().min(1),
  token: z.string().min(1),
  port: z.number().int().positive(),
  caption: z.string().max(200).optional(),
  /** The app's entry page when it is not at '/'. Bounded, not shape-checked:
   *  the announce sanitizes it once (sanitizePreviewPath) and this schema
   *  describes what goes OUT, not what an agent may send in. */
  path: z.string().max(PREVIEW_PATH_MAX).optional(),
  /** The query parameter the token rides in, when it is not Blaxel's `bl_preview_token` (a boat link: `_token`).
   *  Absent on a Blaxel link. */
  tokenParam: z.string().min(1).max(32).optional(),
});

export const TaskPreviewClosedPayloadSchema = z.object({
  taskId: z.string().min(1),
  /** Which app port closed — optional for one release (pre-pool emits had no port). */
  port: z.number().int().positive().optional(),
});

/** One task_apps row. snake_case: these are the stored keys, passed through
 *  verbatim (mirrors TaskApp in types/live-preview.ts). */
export const TaskAppSchema = z.object({
  task_id: z.string(),
  port: z.number().int(),
  caption: z.string().optional(),
  path: z.string().optional(),
  command: z.string().optional(),
  cwd: z.string().optional(),
  state: z.enum(['running', 'stopped']),
  announced_at: z.string(),
  last_viewed_at: z.string().nullable(),
  stopped_at: z.string().nullable(),
  stop_reason: z.enum(['inactivity', 'user', 'replaced', 'agent']).nullable(),
});

/** apps:changed — the user's whole app list, every time (level-triggered). */
export const AppsChangedPayloadSchema = z.object({ apps: z.array(TaskAppSchema) });

/** apps:view — frontend → server: someone is looking at the app on this port. */
export const AppsViewPayloadSchema = z.object({ port: z.number().int() });
